import React, { useEffect, useRef } from "react";
import "./BatteryEducation.css";

const BatteryEducation = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observerOptions = {
      threshold: 0.2, // Triggers when 20% of the card is visible
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-visible");
        }
      });
    }, observerOptions);

    // Target all cards within this section
    const cards = sectionRef.current.querySelectorAll(".chem-card");
    cards.forEach((card) => observer.observe(card));

    return () => observer.disconnect();
  }, []);

  return (
    <section className="education-section" ref={sectionRef}>
      <div className="education-container">
        
        <div className="section-header">
          <h2 className="section-title">Know Your Chemistry</h2>
          <p className="section-subtitle">
            Not all batteries are created equal. Choose the exact power architecture for your specific application.
          </p>
        </div>

        <div className="chem-grid">
          {/* Card 1: NMC - Sweeps in from the Left */}
          <div className="chem-card li-ion">
            <div className="icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-zap"><polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2"/></svg>
            </div>
            <h3>Lithium-Ion (NMC)</h3>
            <p className="chem-desc">
              The king of energy density. Engineered for high-performance applications where <strong>weight and footprint</strong> are critical constraints.
            </p>
            <ul className="chem-specs">
              <li><span className="spec-label">Energy Density</span> <span className="spec-value">High</span></li>
              <li><span className="spec-label">Weight</span> <span className="spec-value">Ultra-Light</span></li>
              <li><span className="spec-label">Cycle Life</span> <span className="spec-value">~800-1000 Cycles</span></li>
              <li><span className="spec-label">Optimal For</span> <span className="spec-value">E-Bikes, Drones</span></li>
            </ul>
          </div>

          {/* Card 2: LFP - Rises from the Bottom */}
          <div className="chem-card lifepo4">
            <div className="icon-wrapper">
               <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-shield-check"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10"/><path d="m9 12 2 2 4-4"/></svg>
            </div>
            <h3>LiFePO4 (LFP)</h3>
            <p className="chem-desc">
              The safety champion. Unmatched thermal stability offering up to <strong>4x the lifespan</strong> of standard lithium configurations.
            </p>
            <ul className="chem-specs">
              <li><span className="spec-label">Energy Density</span> <span className="spec-value">Moderate</span></li>
              <li><span className="spec-label">Weight</span> <span className="spec-value">Heavier</span></li>
              <li><span className="spec-label">Cycle Life</span> <span className="spec-value">2000-4000+ Cycles</span></li>
              <li><span className="spec-label">Optimal For</span> <span className="spec-value">Inverters, Solar</span></li>
            </ul>
          </div>

          {/* Card 3: Custom - Sweeps in from the Right */}
          <div className="chem-card custom-pack">
            <div className="icon-wrapper">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-cpu"><rect width="16" height="16" x="4" y="4" rx="2"/><rect width="6" height="6" x="9" y="9" rx="1"/><path d="M15 2v2"/><path d="M15 20v2"/><path d="M2 15h2"/><path d="M2 9h2"/><path d="M20 15h2"/><path d="M20 9h2"/><path d="M9 2v2"/><path d="M9 20v2"/></svg>
            </div>
            <h3>Why Build Custom?</h3>
            <p className="chem-desc">
              Off-the-shelf solutions rarely fit perfectly. A custom pack guarantees the <strong>exact voltage and form factor</strong> required.
            </p>
            <ul className="chem-specs">
              <li><span className="spec-label">Fit</span> <span className="spec-value">Tailored Dimensions</span></li>
              <li><span className="spec-label">Power</span> <span className="spec-value">Exact V & Ah Rating</span></li>
              <li><span className="spec-label">Quality</span> <span className="spec-value">Premium Cells & BMS</span></li>
              <li><span className="spec-label">Output</span> <span className="spec-value">Zero Compromise</span></li>
            </ul>
          </div>
        </div>

      </div>
    </section>
  );
};

export default BatteryEducation;